import { TAssetOperationTypeEnum } from '@/shared/hooks/use-assets-app-sync-events/use-assets-app-sync-events.types';
import { useInvalidateDeviceTags } from '@/shared/hooks/use-invalidate-asset-tags/hooks/use-invalidate-device-tags';
import { useInvalidateDocumentTags } from '@/shared/hooks/use-invalidate-asset-tags/hooks/use-invalidate-document-tags';
import { useInvalidateGatewayTags } from '@/shared/hooks/use-invalidate-asset-tags/hooks/use-invalidate-gateway-tags';
import { useInvalidateGroupTags } from '@/shared/hooks/use-invalidate-asset-tags/hooks/use-invalidate-group-tags';
import { useInvalidateLicenseTags } from '@/shared/hooks/use-invalidate-asset-tags/hooks/use-invalidate-license-tags';
import { useInvalidateLinkTags } from '@/shared/hooks/use-invalidate-asset-tags/hooks/use-invalidate-link-tags';
import { useInvalidateProjectTags } from '@/shared/hooks/use-invalidate-asset-tags/hooks/use-invalidate-project-tags';
import { useInvalidateTagTags } from '@/shared/hooks/use-invalidate-asset-tags/hooks/use-invalidate-tag-tags';
import { useInvalidateVaultTags } from '@/shared/hooks/use-invalidate-asset-tags/hooks/use-invalidate-vault-tags';
import { TInvalidateAssetTagsParams } from '@/shared/hooks/use-invalidate-asset-tags/use-invalidate-asset-tags.types';
import { AssetTypeEnum } from '@/shared/types/types.generated';

export const useInvalidateAssetTags = () => {
  const { invalidateProjectTags, invalidateProjectVersionTags } =
    useInvalidateProjectTags();
  const { invalidateDocumentTags, invalidateDocumentVersionTags } =
    useInvalidateDocumentTags();
  const { invalidateDeviceTags } = useInvalidateDeviceTags();
  const { invalidateGatewayTags } = useInvalidateGatewayTags();
  const { invalidateLicenseTags } = useInvalidateLicenseTags();
  const { invalidateGroupTags } = useInvalidateGroupTags();
  const { invalidateVaultTags, invalidateSecretTags } =
    useInvalidateVaultTags();
  const { invalidateTagTags } = useInvalidateTagTags();
  const { invalidateLinkTags } = useInvalidateLinkTags();

  const invalidateAssetTags = ({
    assetType,
    assetId,
    operationType,
    payload,
  }: TInvalidateAssetTagsParams) => {
    if (
      [
        TAssetOperationTypeEnum.AssetLinked,
        TAssetOperationTypeEnum.AssetLinkUpdated,
        TAssetOperationTypeEnum.AssetUnlinked,
      ].includes(operationType) &&
      assetType !== AssetTypeEnum.Tag
    ) {
      invalidateLinkTags(payload);
      return;
    }

    if (assetType === AssetTypeEnum.Device) {
      invalidateDeviceTags(assetId, operationType);
      return;
    }

    if (assetType === AssetTypeEnum.Document) {
      invalidateDocumentTags(assetId, operationType);
      return;
    }

    if (assetType === AssetTypeEnum.DocumentVersion) {
      invalidateDocumentVersionTags(payload?.documentId);
      return;
    }

    if (assetType === AssetTypeEnum.Gateway) {
      invalidateGatewayTags(assetId, operationType);
      return;
    }

    if (assetType === AssetTypeEnum.License) {
      invalidateLicenseTags(assetId, operationType);
      return;
    }

    if (assetType === AssetTypeEnum.Project) {
      invalidateProjectTags(assetId, operationType);
      return;
    }

    if (assetType === AssetTypeEnum.ProjectVersion) {
      invalidateProjectVersionTags(payload?.projectId, operationType);
      return;
    }

    if (assetType === AssetTypeEnum.ResourceGroup) {
      invalidateGroupTags(operationType, payload?.parentGroupId);
      return;
    }

    if (assetType === AssetTypeEnum.Vault) {
      invalidateVaultTags(assetId, operationType);
      return;
    }

    if (assetType === AssetTypeEnum.Secret) {
      invalidateSecretTags(assetId, operationType, payload?.vaultId);
      return;
    }

    if (assetType === AssetTypeEnum.Tag) {
      invalidateTagTags(operationType, payload);
      return;
    }
  };

  return {
    invalidateAssetTags,
  };
};
